// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { ObservableInput, ObservableOutput } from 'thehive-sdk';
import { splitObservable, trimTags, useLocalMessage } from '@Helpers/';
import { ObservableForm, ObservableValuesForm } from '@Organisms/Observable/ObservableCreate/ObservableForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { ICONS } from '@Constants/iconsApp';
import { Space } from 'antd';

interface ObservableCreateProps {
    onFinish: (observable: ObservableInput) => Promise<any>;
    triggerElem?: ReactElement;
    defaultValues?: ObservableOutput & { multiline?: boolean };
    defaultVisible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
    visible?: boolean;
}

export const ObservableCreate = ({
    onFinish,
    triggerElem,
    defaultValues,
    defaultVisible = false,
    onVisibleChange,
    visible,
}: ObservableCreateProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: !!defaultValues || defaultVisible,
        fieldsTouchedInit: !!defaultValues || defaultVisible,
    });

    const onFinishHandler = ({
        type = '',
        value = '',
        tlp = 2,
        pap = 1,
        ioc = false,
        sighted = false,
        sightedAt = undefined,
        tags = [],
        description = '',
        ignoreSimilarity = false,
        multiline = false,
        isZip = false,
        zipPassword,
        attachment = undefined,
    }: ObservableValuesForm): Promise<any> => {
        const observableToBeCreated: ObservableInput = {
            dataType: type,
            tlp,
            pap,
            ioc,
            sighted,
            tags: trimTags(tags),
            sightedAt,
            ignoreSimilarity,
            message: description.trim(),
            data: !attachment ? (multiline ? splitObservable(value) : [value?.trim()]) : undefined,
            attachment,
            isZip,
            zipPassword,
        };

        return onFinish(observableToBeCreated);
    };

    return (
        <THDrawer
            title={
                <Space>
                    <ICONS.ObservableIcon />
                    {i('observable.create.drawer.title')}
                </Space>
            }
            width={'47vw'}
            triggerElem={triggerElem}
            defaultVisible={defaultVisible}
            afterVisibleChange={(visible) => {
                form.resetFields();
                onVisibleChange?.(visible);
            }}
            className="drawer-form"
            visible={visible}
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <ObservableForm
                    form={form}
                    isValid={isValid && fieldsTouched}
                    defaultValues={defaultValues}
                    onFinish={({ another, ...values }) => {
                        return onFinishHandler(values).then((ret) => {
                            if ('failure' in ret) {
                                return;
                            }

                            form.resetFields();
                            setFieldsTouched(false);

                            if (!another) {
                                toggleDrawer();
                                return;
                            }

                            onFormValuesChange();
                        });
                    }}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onCancel={() => {
                        toggleDrawer();
                        setFieldsTouched(false);
                        form.resetFields();
                    }}
                />
            )}
        </THDrawer>
    );
};
